/* Real Shopify cart; never the prototype's localStorage cart. */
if (!customElements.get('chapon-product')) {
  customElements.define('chapon-product', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.config = JSON.parse(this.querySelector('[data-product-config]').textContent);
      this.form = this.querySelector('[data-product-form]');
      this.button = this.querySelector('[data-testid="add-to-cart"]');
      this.status = this.querySelector('[data-product-status]');
      this.querySelectorAll('[data-step], [data-gift-options]').forEach(node => { node.hidden = false; });
      this.classList.add('is-enhanced');
      this.showMedia(this.config.variants.find(v => String(v.id) === this.variantId())?.mediaId || this.querySelector('[data-media-id]')?.dataset.mediaId);
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-media-target]');
        if (thumb) { event.preventDefault(); this.showMedia(thumb.dataset.mediaTarget); }
        const step = event.target.closest('[data-step]');
        if (step) {
          const input = this.form.elements.quantity;
          if (Number(step.dataset.step) > 0) input.stepUp(); else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, { signal });
      this.addEventListener('change', event => {
        if (event.target.name === 'id' && event.target.form === this.form) this.updateVariant();
        if (event.target.name === 'selling_plan' && event.target.form === this.form) this.updateVariant(false);
      }, { signal });
      if (this.config.subscriptionOnly) {
        // Links back to the plan picker (sticky bar, closing block) focus the chosen plan after the jump.
        document.addEventListener('click', event => {
          if (!event.target.closest?.('[data-omakase-to-plan]')) return;
          setTimeout(() => this.planInputs().find(input => input.checked)?.focus({ preventScroll: true }), 400);
        }, { signal });
        this.applyDeepLink();
        // The closing block's interval toggle mirrors the plan radios in the form (one source of truth for the selling plan).
        document.addEventListener('change', event => {
          const mirror = event.target.closest?.('[data-omakase-mirror]');
          if (!mirror) return;
          const input = this.planInputs().find(node => node.value === mirror.value && !node.disabled);
          if (!input) return;
          input.checked = true;
          this.updateVariant(false);
        }, { signal });
        this.pageNodes('[data-omakase-mirror-group]').forEach(node => { node.hidden = false; });
      }
      if (this.config.subscriptionOnly || this.config.omakaseAnnual) {
        // The closing add button submits this form through its own add button, so the same plan, quantity and checks apply.
        document.addEventListener('click', event => {
          const add = event.target.closest?.('[data-omakase-add]');
          if (!add || add.disabled) return;
          event.preventDefault();
          if (!this.form.reportValidity()) return;
          this.source = add;
          this.form.requestSubmit(this.button);
        }, { signal });
      }
      this.form?.addEventListener('submit', event => {
        if (this.config.subscriptionOnly && !this.subscriptionAllocation()) {
          event.preventDefault();
          return;
        }
        // Only this theme's normal add button; app-owned purchase flows are untouched.
        if (this.appWrapping() || !this.button || event.submitter !== this.button) return;
        event.preventDefault();
        this.addToCart();
      }, { signal });
      if (!window.ChaponImages) document.addEventListener('chapon:images-ready', () => {
        this.showMedia(this.querySelector('[data-media-target][aria-current="true"]')?.dataset.mediaTarget || this.querySelector('[data-media-id]')?.dataset.mediaId);
      }, { once: true, signal });
      if (this.querySelector('[data-noshi-config]') && window.ChaponProductNoshi) this.noshi = new window.ChaponProductNoshi(this);
      this.connectPressFeedback();
      this.updateVariant(false);
      this.connectBar();
      this.connectAnyGift();
      this.connectGiftPrep();
      document.addEventListener('shopify:section:load', () => this.connectBar(), { signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resetPressFeedback();
      clearTimeout(this.successTimer);
      this.added = this.addAcknowledged = false;
      this.barController?.abort();
      this.observer?.disconnect();
      this.hideObserver?.disconnect();
      this.anygiftObserver?.disconnect();
      if (this.bar) { this.bar.hidden = true; this.bar.classList.remove('is-shown'); }
      this.updateBarClearance();
    }
    connectAnyGift() {
      this.anygiftObserver?.disconnect();
      if (this.config?.subscriptionOnly || this.config?.omakaseAnnual) return;
      if (document.documentElement.lang !== 'ja' || this.dataset.orderOptionsMode !== 'apps' || !this.form) return;
      const rewrite = () => {
        this.form.querySelectorAll('.anygift-e-gift-modal__open-button').forEach(button => {
          if (button.textContent.trim() === '住所を知らない相手にeギフトで贈る') {
            button.textContent = '友だちにLINEなどでギフトを贈る';
          }
        });
      };
      rewrite();
      // Keep watching the form for late mounts and replacements, not the whole document.
      // Our own text mutation is a no-op on the next callback; unknown labels stay app-owned.
      this.anygiftObserver = new MutationObserver(rewrite);
      this.anygiftObserver.observe(this.form, { childList: true, characterData: true, subtree: true });
    }
    appWrapping() {
      return !this.noshi?.selected() && !this.config?.subscriptionOnly && !this.config?.omakaseAnnual && this.dataset?.orderOptionsMode === 'apps' && this.dataset.wrappingMode !== 'theme';
    }
    connectPressFeedback() {
      if (!this.button || this.dataset.wrappingMode !== 'theme') return;
      const signal = this.controller.signal;
      this.button.addEventListener('pointerdown', event => {
        if (event.button === 0 && event.isPrimary) this.startPressFeedback({ pointerId: event.pointerId });
      }, { signal });
      this.button.addEventListener('keydown', event => {
        if (!event.repeat && (event.key === 'Enter' || event.key === ' ')) this.startPressFeedback({ key: event.key });
      }, { signal });
      window.addEventListener('pointercancel', event => {
        if (this.pressing?.pointerId === event.pointerId) this.resetPressFeedback();
      }, { signal, capture: true });
      window.addEventListener('pointerup', event => {
        // Touch may implicitly capture the pointer; use the release position, not its target.
        if (this.pressing?.pointerId === event.pointerId &&
          !this.button.contains(document.elementFromPoint(event.clientX, event.clientY))) this.resetPressFeedback();
      }, { signal, capture: true });
      this.button.addEventListener('keyup', event => {
        if (this.pressing?.key !== event.key) return;
        // Space's native click happens after keyup listeners. Let submit take over first.
        clearTimeout(this.pressTimer);
        this.pressTimer = setTimeout(() => this.resetPressFeedback(), 0);
      }, { signal });
      this.button.addEventListener('blur', () => this.resetPressFeedback(), { signal });
    }
    startPressFeedback(press) {
      const variant = this.config.variants.find(v => String(v.id) === this.variantId());
      if (this.dataset.wrappingMode !== 'theme' || this.appWrapping() || this.busy || this.pressing ||
        this.button.disabled || !variant?.available || !this.form.checkValidity()) return;
      this.pressing = press;
      this.updateBuyButton(variant);
      this.pressTimer = setTimeout(() => this.resetPressFeedback(), 800);
    }
    resetPressFeedback(render = true) {
      clearTimeout(this.pressTimer);
      if (!this.pressing) return;
      this.pressing = undefined;
      if (render && this.isConnected) this.updateVariant(false);
    }
    connectGiftPrep() {
      const card = this.form?.querySelector('[data-gift-prep]');
      if (!card) return;
      const input = card.querySelector('[name="gift_option"]');
      const render = () => {
        const selected = [];
        const choices = input.value.split(',');
        card.querySelectorAll('[data-gift-choice]').forEach(choice => {
          const pressed = choices.includes(choice.dataset.giftChoice);
          choice.setAttribute('aria-pressed', String(pressed));
          choice.querySelector('[data-gift-label]').textContent = pressed ? this.config.labels.selectedBag : this.config.labels.addBag;
          choice.querySelector('[data-gift-check]').hidden = !pressed;
          if (pressed) selected.push(choice);
        });
        const summary = selected.length ? this.config.labels.bagSummary.replace('__BAG_TITLE__', () => selected.map(choice => choice.dataset.bagTitle).join('・')) : '';
        const cardSummary = card.querySelector('[data-bag-summary-card]');
        [this.querySelector('[data-bag-summary]'), this.bar?.querySelector('[data-bag-summary]'), cardSummary].forEach(node => {
          if (node) {
            node.textContent = node === cardSummary && selected.length ? (this.giftAttached ?
              card.dataset.bagAttached.replace('__BAG_TITLE__', () => selected.map(choice => choice.dataset.bagTitle).join('・')) :
              this.giftPreselectedOnly ? this.config.labels.bagSummaryAfterAdd : summary) : summary;
            node.hidden = !selected.length;
          }
        });
        this.updateBarClearance();
      };
      this.renderGiftPrep = render;
      const reset = () => { input.value = 'none'; this.giftSelectionVersion = (this.giftSelectionVersion || 0) + 1; this.giftAttached = this.giftPreselectedOnly = false; render(); };
      reset();
      window.addEventListener('pageshow', event => { if (event.persisted) reset(); }, { signal: this.controller.signal });
      card.hidden = false;
      this.noshi?.updateEligibility();
      card.addEventListener('click', event => {
        const button = event.target.closest('[data-gift-choice]');
        if (!button || button.disabled || this.busy) return;
        const selected = new Set(input.value.split(','));
        const choice = button.dataset.giftChoice;
        const enabled = !selected.has(choice);
        if (enabled) selected.add(choice); else selected.delete(choice);
        const gift = ['wrapping', 'paper'].filter(type => selected.has(type)).join(',') || 'none';
        this.chooseGift(gift, input, render, choice, enabled);
      }, { signal: this.controller.signal });
    }
    async chooseGift(gift, input, render, choice, selected) {
      if (this.noshi?.selected() || this.dataset.wrappingMode !== 'theme' || typeof window.ChaponCart?.wrapProduct !== 'function') {
        input.value = gift; render(); return;
      }
      const version = this.giftSelectionVersion;
      const variantId = this.variantId();
      const previous = { value: input.value, attached: this.giftAttached, preselected: this.giftPreselectedOnly };
      input.value = gift;
      render();
      this.busy = true;
      this.updateVariant(false);
      this.querySelector('[data-cart-recovery]').hidden = true;
      const apply = main => {
        if (main) (this.addedLines ||= {})[variantId] = main.key;
        else if (this.addedLines) delete this.addedLines[variantId];
        if (version !== this.giftSelectionVersion || variantId !== this.variantId()) return;
        if (main) {
          const value = main.properties?.[this.config.propertyName];
          input.value = ['wrapping', 'paper'].filter(choice => value?.split('・').includes(this.config.options[choice]?.value)).join(',') || 'none';
        } else input.value = gift;
        this.giftAttached = Boolean(main?.properties?._wrap_group);
        this.giftPreselectedOnly = !main;
      };
      try {
        const main = await window.ChaponCart.wrapProduct({ key: this.addedLines?.[variantId], variantId,
          gift, choice, selected, options: this.config.options, propertyName: this.config.propertyName });
        apply(main);
        this.status.textContent = '';
      } catch (error) {
        if (error.main !== undefined) {
          apply(error.main);
          if (!error.main) { input.value = 'none'; this.giftAttached = this.giftPreselectedOnly = false; }
        } else if (version === this.giftSelectionVersion && variantId === this.variantId()) {
          input.value = previous.value; this.giftAttached = previous.attached; this.giftPreselectedOnly = previous.preselected;
        }
        this.status.textContent = `${error.message || this.config.labels.error} ${this.config.labels.error}`;
        this.querySelector('[data-cart-recovery]').hidden = false;
        this.status.focus();
      } finally {
        this.busy = false;
        if (this.isConnected) { render(); this.updateVariant(false); }
      }
    }
    variantId() {
      return String(this.form.elements.id.value);
    }
    showMedia(id) {
      if (!id) return;
      const slides = [...this.querySelectorAll('[data-media-id]')];
      const index = slides.findIndex(node => node.dataset.mediaId === String(id));
      if (index < 0) return;
      const initial = !this.mediaRequest;
      const request = this.mediaRequest = (this.mediaRequest || 0) + 1;
      const image = slides[index].querySelector('img');
      const show = () => {
        if (request !== this.mediaRequest || !this.isConnected) return;
        slides.forEach(node => {
          node.hidden = node.dataset.mediaId !== String(id);
          if (node.hidden) node.querySelectorAll('video').forEach(video => video.pause());
        });
        this.querySelectorAll('[data-media-target]').forEach(node => node.setAttribute('aria-current', String(node.dataset.mediaTarget === String(id))));
        if (window.ChaponImages) window.ChaponImages.ready(image).then(() => {
          if (request !== this.mediaRequest || !this.isConnected) return;
          for (let offset = 1; offset <= Math.min(2, slides.length - 1); offset++) window.ChaponImages.preload(slides[(index + offset) % slides.length].querySelector('img'));
        });
      };
      // Reserve the initial gallery immediately; only later switches retain an existing photo.
      if (!initial && window.ChaponImages && image) window.ChaponImages.preload(image).then(show);
      else show();
    }
    pageNodes(selector) {
      // Subscription copies outside this element (later sections); none in non-DOM test doubles.
      return typeof globalThis.document?.querySelectorAll === 'function' ? [...document.querySelectorAll(selector)] : [];
    }
    applyDeepLink() {
      // LINE/ad links: ?plan=monthly|bimonthly preselects an interval (a real ?selling_plan= wins); ?for=gift moves the
      // gift note above the button. Plans are matched by interval (data-omakase-step), never by a hard-coded id.
      const params = new URLSearchParams(globalThis.location?.search || '');
      const step = { monthly: '1', bimonthly: '2' }[params.get('plan')];
      const input = step && !params.has('selling_plan') && this.planInputs().find(node => node.dataset.omakaseStep === step && !node.disabled);
      if (input) input.checked = true;
      if (params.get('for') !== 'gift') return;
      this.querySelectorAll('[data-omakase-gift-hint]').forEach(node => { node.hidden = true; });
      this.querySelectorAll('[data-omakase-gift-mode]').forEach(node => { node.hidden = false; });
    }
    planInputs() {
      return [...(this.querySelector('[data-selling-plan]')?.querySelectorAll('input[name="selling_plan"]') || [])];
    }
    subscriptionAllocation() {
      const variant = this.config.variants.find(v => String(v.id) === this.variantId());
      const value = this.planInputs().find(input => input.checked && !input.disabled)?.value;
      return variant?.allocations?.find(plan => String(plan.id) === value);
    }
    updateSubscription(variant) {
      const picker = this.querySelector('[data-selling-plan]');
      if (!picker) return;
      // Radios are rendered from the selected variant's plans; another variant keeps only the plans it also offers.
      const plans = variant.allocations || [];
      const planFor = input => plans.find(plan => String(plan.id) === input.value);
      const inputs = this.planInputs();
      inputs.forEach(input => { input.disabled = !planFor(input); });
      if (!inputs.some(input => input.checked && !input.disabled)) {
        const fallback = inputs.find(input => !input.disabled && planFor(input).name.includes(this.config.defaultPlanName)) || inputs.find(input => !input.disabled);
        if (fallback) fallback.checked = true;
      }
      picker.disabled = picker.hidden = !inputs.some(input => !input.disabled);
      const allocation = this.subscriptionAllocation();
      this.querySelector('[data-subscription-unavailable]').hidden = Boolean(allocation);
      this.querySelectorAll?.('.omakase-price, [data-omakase-needs-plan]').forEach(node => { node.hidden = !allocation; });
      const price = this.querySelector('[data-omakase-price]');
      if (price) price.textContent = allocation?.priceHtml || this.config.subscriptionPricePending || this.config.subscriptionUnavailable;
      // Page-wide copies that follow the chosen plan: month bars and summary, the yearly amount, the next-box note, the closing line.
      const id = allocation ? String(allocation.id) : '';
      this.pageNodes('[data-omakase-plan]').forEach(node => { node.hidden = node.dataset.omakasePlan !== id; });
      this.pageNodes('[data-omakase-plan-mark]').forEach(node => node.classList.toggle('is-selected', node.dataset.omakasePlanMark === id));
      this.pageNodes('[data-omakase-mirror]').forEach(node => {
        node.checked = node.value === id;
        node.disabled = !inputs.some(input => input.value === node.value && !input.disabled);
      });
    }
    updateVariant(updateUrl = true) {
      const variant = this.config.variants.find(v => String(v.id) === this.variantId());
      if (!variant) return;
      this.noshi?.updateEligibility();
      this.toggleAttribute('data-anygift-hidden', this.hasAttribute('data-anygift-ineligible') || !variant.available);
      const soldOut = this.querySelector('[data-egift-sold-out]');
      if (soldOut) {
        soldOut.hidden = variant.available;
        if (this.button) this.button.hidden = !variant.available;
      }
      const price = this.querySelector('[data-product-price]');
      if (this.config.subscriptionOnly) this.updateSubscription(variant);
      if (price) price.innerHTML = this.config.subscriptionOnly ? (this.subscriptionAllocation()?.priceHtml || '') : variant.priceHtml;
      const sku = this.querySelector('[data-sku]');
      if (sku) { sku.hidden = !variant.sku; sku.querySelector('span').textContent = variant.sku || ''; }
      const label = this.updateBuyButton(variant);
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        this.showMedia(variant.mediaId);
      }
      if (this.bar && this.button) {
        const allocation = this.config.subscriptionOnly && this.subscriptionAllocation();
        this.bar.querySelector('[data-bar-price]').innerHTML = this.config.subscriptionOnly ? (allocation?.priceHtml || '') : variant.priceHtml;
        const plan = this.bar.querySelector('[data-bar-plan]');
        if (plan) plan.textContent = allocation?.name || '';
        const proxy = this.bar.querySelector('[data-bar-add]');
        if (proxy) {
          proxy.disabled = this.button.disabled;
          if (this.dataset.wrappingMode === 'theme') proxy.setAttribute('aria-busy', String(Boolean(this.busy)));
          // The subscription's main label is long; the bar keeps the short add label for the same action.
          proxy.querySelector('span').textContent = (this.config.subscriptionOnly || this.config.omakaseAnnual) && label === this.config.labels.add ? this.config.labels.barAdd || label : label;
        }
      }
      if (this.button) this.pageNodes('[data-omakase-add]').forEach(node => { node.disabled = this.button.disabled; });
    }
    updateBuyButton(variant) {
      if (!this.button) return;
      const feedback = this.dataset.wrappingMode === 'theme';
      const subscriptionBlocked = this.config.subscriptionOnly && !this.subscriptionAllocation();
      const label = subscriptionBlocked ? this.config.subscriptionUnavailable : !variant.available ? this.config.labels.soldOut :
        feedback && this.added ? this.config.labels.addedHeading : feedback && this.busy && !this.addAcknowledged ? this.config.labels.adding : this.config.labels.add;
      this.button.disabled = subscriptionBlocked || !variant.available || Boolean(this.busy);
      const pressing = feedback && this.pressing && variant.available;
      if (feedback) this.button.setAttribute('aria-busy', String(Boolean(this.busy || pressing)));
      this.button.querySelector('[data-buy-label]').textContent = pressing ? this.config.labels.adding : label;
      return label; // The sticky proxy only reflects the real add, never a pending main-button press.
    }
    updateBarClearance() {
      const height = this.bar && !this.bar.hidden ? this.bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--buy-bar-h', `${height}px`);
    }
    connectBar() {
      this.barController?.abort();
      this.observer?.disconnect();
      this.hideObserver?.disconnect();
      this.barController = new AbortController();
      this.bar = document.querySelector('[data-section="sticky-buy-bar"]');
      if (this.bar) { this.bar.hidden = true; this.bar.classList.remove('is-shown'); }
      this.updateBarClearance();
      // Do not submit the original button behind Globo's cloned app-owned button.
      if (this.appWrapping() || !this.bar || !this.button || !('IntersectionObserver' in window)) return;
      this.bar.querySelector('[data-bar-add]')?.addEventListener('click', event => {
        if (!this.form.reportValidity()) return;
        this.source = event.currentTarget; // Safari does not focus a clicked button.
        this.form.requestSubmit(this.button);
      }, { signal: this.barController.signal });
      const bar = { past: false, covered: new Set() };
      const apply = () => {
        // A subscription without a selling plan has no price to show, so no bar.
        const visible = bar.past && bar.covered.size === 0 && (!this.config.subscriptionOnly || Boolean(this.subscriptionAllocation()));
        this.bar.hidden = !visible;
        this.bar.classList.toggle('is-shown', visible);
        this.updateBarClearance();
      };
      // The root extends far below the viewport, so "below the fold" still counts as intersecting
      // and a jump straight past the button (anchor link, fast scroll) still reports a change.
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        bar.past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        apply();
      }, { rootMargin: '0px 0px 100000px 0px' });
      this.observer.observe(this.button);
      // Subscription and 年間便: the bar steps aside over the terms, the closing purchase block and the footer.
      const hiders = this.config.subscriptionOnly || this.config.omakaseAnnual ? [...document.querySelectorAll('[data-omakase-bar-hide], [data-section="site-footer"]')] : [];
      if (hiders.length) {
        this.hideObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting) bar.covered.add(entry.target); else bar.covered.delete(entry.target); });
          apply();
        });
        hiders.forEach(node => this.hideObserver.observe(node));
      }
      window.addEventListener('resize', () => this.updateBarClearance(), { signal: this.barController.signal });
      this.updateVariant(false);
      this.renderGiftPrep?.();
    }
    showAdded() {
      if (this.dataset.wrappingMode !== 'theme' || !this.isConnected) return;
      this.added = this.addAcknowledged = true;
      this.status.textContent = this.config.labels.added;
      this.updateVariant(false);
      this.renderGiftPrep?.();
      clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => {
        this.added = false;
        if (this.isConnected) this.updateVariant(false);
      }, 2000);
    }
    nativeAdd(items) {
      // Submit one native multi-item POST, including the pairing properties.
      const fields = [...this.form.elements].filter(input => /^(id|quantity|selling_plan|gift_option|properties\[)/.test(input.name));
      const states = fields.map(input => input.disabled);
      fields.forEach(input => { input.disabled = true; });
      const inputs = [];
      const append = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = String(value);
        this.form.append(input); inputs.push(input);
      };
      items.forEach((item, index) => {
        for (const [key, value] of Object.entries(item)) {
          if (key === 'properties') {
            for (const [property, text] of Object.entries(value)) append(`items[${index}][properties][${property}]`, text);
          } else append(`items[${index}][${key}]`, value);
        }
      });
      window.ChaponAdsRemarketing?.remember(items[0].id, items[0].quantity);
      try { HTMLFormElement.prototype.submit.call(this.form); }
      finally {
        inputs.forEach(input => input.remove());
        fields.forEach((input, index) => { input.disabled = states[index]; });
      }
    }
    flightImage() {
      // The selected variant's image as shown in the gallery, else the product's first image.
      // Decoration only: a missing image must never stand in the way of the add.
      const variant = this.config.variants?.find(v => String(v.id) === this.variantId());
      return [`[data-media-id="${variant?.mediaId}"] img`, '[data-media-id]:not([hidden]) img', '.gallery__main img']
        .map(selector => this.querySelector(selector)).find(image => image?.complete && image.naturalWidth);
    }
    async addToCart() {
      const source = this.source || this.button;
      this.source = undefined;
      if (this.config.subscriptionOnly && !this.subscriptionAllocation()) return;
      if (this.appWrapping() || this.busy || !this.button || this.button.disabled || !this.form.reportValidity()) return;
      const noshi = this.noshi?.payload();
      if (noshi === false) return;
      clearTimeout(this.successTimer);
      this.added = this.addAcknowledged = false;
      this.busy = true;
      this.resetPressFeedback(false);
      this.updateVariant(false);
      this.status.textContent = this.dataset.wrappingMode === 'theme' ? this.config.labels.adding : '';
      this.querySelector('[data-cart-recovery]').hidden = true;
      try {
        const data = new FormData(this.form);
        // 年間便: one per order (each order ships one box a month), so a second add never raises the cart line above 1.
        if (this.config.omakaseAnnual) {
          const cart = await fetch(`${this.dataset.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store' }).then(response => response.ok ? response.json() : null).catch(() => null);
          if (cart?.items?.some(item => String(item.variant_id ?? item.id) === String(data.get('id')))) {
            this.status.textContent = this.config.labels.annualInCart;
            this.querySelector('[data-cart-recovery]').hidden = false;
            return;
          }
        }
        const properties = this.config.subscriptionOnly ? { _omakase: 'true' } : {};
        for (const [name, value] of data.entries()) {
          const match = /^properties\[(.+)\]$/.exec(name);
          if (!this.config.subscriptionOnly && match && typeof value === 'string' &&
            !((noshi || this.dataset?.wrappingMode === 'theme') && /^(?:_gpo_|_has_gpo$)/.test(match[1]))) properties[match[1]] = value;
        }
        const gift = this.config.subscriptionOnly ? 'none' : data.get('gift_option') || 'none';
        const items = ChaponProductCart.buildItems({
          variantId: data.get('id'), quantity: this.config.omakaseAnnual ? 1 : data.get('quantity') || 1,
          properties, gift, options: this.config.options, propertyName: this.config.propertyName, noshi,
          groupId: gift !== 'none' ? crypto.randomUUID() : undefined
        });
        if (data.get('selling_plan')) items[0].selling_plan = data.get('selling_plan');
        if (typeof window.ChaponCart?.add === 'function') {
          await window.ChaponCart.add(items, { opener: this.bar?.contains(document.activeElement) || document.activeElement?.closest?.('[data-omakase-add]') ? document.activeElement : this.button, source, image: this.flightImage() || null, primaryVariantId: items[0].id, onAdded: response => {
            const main = (response?.items || (response ? [response] : [])).find(item => String(item.variant_id ?? item.id) === String(items[0].id));
            if (main?.key) (this.addedLines ||= {})[String(items[0].id)] = main.key;
            this.giftAttached = Boolean(main?.properties?._wrap_group);
            this.giftPreselectedOnly = false;
            this.showAdded();
          } });
          if (!this.isConnected) return;
          this.status.textContent = this.config.labels.added;
        } else {
          if (noshi) throw new Error(this.noshi.config.labels.unavailable);
          this.nativeAdd(items);
        }
      } catch (error) {
        clearTimeout(this.successTimer);
        this.added = this.addAcknowledged = false;
        if (error.name === 'AbortError') return;
        this.status.textContent = `${error.message || this.config.labels.error} ${this.config.labels.error}`;
        this.querySelector('[data-cart-recovery]').hidden = false;
        this.status.focus();
        // Never automatically retry: 422 or a lost response can leave a partially changed cart.
      } finally {
        this.busy = false;
        if (this.isConnected) this.updateVariant(false);
      }
    }
  });
}
